import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, UNSAFE_NavigationContext } from 'react-router-dom';
import { useContext } from 'react';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Container,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeploymentDetail from './DeploymentDetail';
import ChannelIntegrations from './ChannelIntegrations';
import ToolsIntegrations from './ToolsIntegrations';
import BusinessActionSettings from './BusinessActionSettings';
import LoadProgress from '../../common/LoadProgress.js';
import SystemAlert from '../../common/SystemAlert.js';
import Cookies from '../../common/Cookies.js';
import axios from 'axios';
import ChatMain from '../chat/ChatMain.js';
import UnsavedChangesDialog from './UnsavedChangesDialog';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, padding = 3, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`agent-tabpanel-${index}`}
      aria-labelledby={`agent-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: padding }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function AgentDetail({ agentIdFromWizard, onCustomizationSaved, onCancel }) {
  // For navigation blocking (React Router v6+)
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const { agentId } = useParams();
  const [mainAgentId, setMainAgentId] = useState(null);
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  const pendingTabValue = useRef(null);
  const chatMainRef = useRef();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedCustomization, setLastSavedCustomization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agent, setAgent] = useState(null);
  const [alert, setAlert] = useState(null);
  const [selectedInstructionId, setSelectedInstructionId] = useState(null);

  useEffect(() => {
    var anAgentId = null;

    console.log('agentIdFromWizard', agentIdFromWizard);

    if (!agentId && agentIdFromWizard)
      anAgentId = agentIdFromWizard;
    if (agentId)
      anAgentId = agentId;
    setMainAgentId(anAgentId);

    if (anAgentId) {
      fetchAgentDetail(anAgentId);
    }
  }, []);

  const fetchAgentDetail = async (anAgentId) => {
    try {
      setLoading(true);
      const response = await axios.post('/agents/getAgentDetail', null, { params: { agentId: anAgentId } });
      console.log('Agent detail response:', response);
      setAgent(response.data);
      if (response.data && response.data.instructionId) {
        setSelectedInstructionId(response.data.instructionId);
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      setAlert({ severity: 'error', message: 'Failed to load agent details' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    if (newValue !== tabValue && hasUnsavedChanges) {
      pendingTabValue.current = newValue;
      setUnsavedDialogOpen(true);
    } else {
      setTabValue(newValue);
    }
  };

  // Save handler for UnsavedChangesDialog
  const handleUnsavedSave = async () => {
    if (chatMainRef.current && chatMainRef.current.saveAgentCustomization) {
      await chatMainRef.current.saveAgentCustomization();
    }
    setHasUnsavedChanges(false);
    setUnsavedDialogOpen(false);
    setTabValue(pendingTabValue.current);
    pendingTabValue.current = null;
  };
  // Discard handler for UnsavedChangesDialog
  const handleUnsavedDiscard = () => {
    if (chatMainRef.current && chatMainRef.current.resetCustomization) {
      chatMainRef.current.resetCustomization();
    }
    setHasUnsavedChanges(false);
    setUnsavedDialogOpen(false);
    setTabValue(pendingTabValue.current);
    pendingTabValue.current = null;
  };
  // Cancel handler for UnsavedChangesDialog
  const handleUnsavedCancel = () => {
    setUnsavedDialogOpen(false);
    pendingTabValue.current = null;
  };

  // Placeholder components for the tabs
  const CustomizeChatBot = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Customize Agent</Typography>
      <Typography variant="body1" sx={{ mt: 2 }}>
        Customize your Agent's appearance and behavior here.
      </Typography>
    </Box>
  );


  const customizationSaved = async () => {
    if (onCustomizationSaved) {
      onCustomizationSaved();
    }
    // Refetch agent details to get latest customization
    if (mainAgentId) {
      await fetchAgentDetail(mainAgentId);
    }
    setAlert({ severity: 'success', message: 'Agent customization saved successfully.' });
    setTimeout(() => setAlert(null), 3000);
  };
  // Warn on browser refresh if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Intercept Back to Agents button
  const handleBackToAgents = (e) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => navigate('/agents'));
      setUnsavedDialogOpen(true);
    } else {
      navigate('/agents');
    }
  };

  // Navigation guard for all navigation (React Router v6+)
  useEffect(() => {
    if (!navigator || !navigator.block) return;
    const unblock = navigator.block((tx) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(() => () => tx.retry());
        setUnsavedDialogOpen(true);
        return false;
      }
      return true;
    });
    return () => unblock();
  }, [hasUnsavedChanges, navigator]);

  // When user confirms save/discard, resume pending navigation
  const handleUnsavedSaveNav = async () => {
    if (chatMainRef.current && chatMainRef.current.saveAgentCustomization) {
      await chatMainRef.current.saveAgentCustomization();
    }
    setHasUnsavedChanges(false);
    setUnsavedDialogOpen(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    } else {
      setTabValue(pendingTabValue.current);
      pendingTabValue.current = null;
    }
  };
  const handleUnsavedDiscardNav = () => {
    if (chatMainRef.current && chatMainRef.current.resetCustomization) {
      chatMainRef.current.resetCustomization();
    }
    setHasUnsavedChanges(false);
    setUnsavedDialogOpen(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    } else {
      setTabValue(pendingTabValue.current);
      pendingTabValue.current = null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {alert && (
        <SystemAlert open={alert !== null} severity={alert.severity} message={alert.message} onClose={() => setAlert(null)} />
      )}

      {loading && <LoadProgress open={loading} />}

      {!agentIdFromWizard &&
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" component="h1">
            {agent ? `Agent: ${agent.agentName}` : 'Agent: New'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToAgents}
          >
            Back to Agents
          </Button>
        </Box>
      }
      <Paper elevation={0}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="agent detail tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            {/* <Tab label="Agent Info" /> */}
            {mainAgentId && <Tab label="Customize Agent" />}
            {mainAgentId && <Tab label="Deployment Detail" />}
            {mainAgentId && <Tab label="Channel Integrations" />}
            {mainAgentId && <Tab label="Tools Integrations" />}
            {mainAgentId && <Tab label="Business Actions" id="agent-tab-4" aria-controls="agent-tabpanel-4" />}
          </Tabs>
        </Box>

        {/* <TabPanel value={tabValue} index={0}>
          <AgentInfo agentId={agentId} />
        </TabPanel> */}

        {mainAgentId && (
          <TabPanel value={tabValue} index={0}>
            <ChatMain
              ref={chatMainRef}
              agentId={mainAgentId}
              customizationFromDb={agent?.customization}
              defaultBotTitle={agent?.agentName}
              selectedInstructionId={selectedInstructionId}
              setSelectedInstructionId={setSelectedInstructionId}
              onCustomizationSaved={customizationSaved}
              hasUnsavedChanges={hasUnsavedChanges}
              setHasUnsavedChanges={setHasUnsavedChanges}
              lastSavedCustomization={lastSavedCustomization}
              setLastSavedCustomization={setLastSavedCustomization}
            />
          </TabPanel>
        )}
        <UnsavedChangesDialog
          open={unsavedDialogOpen}
          onSave={pendingNavigation ? handleUnsavedSaveNav : handleUnsavedSave}
          onDiscard={pendingNavigation ? handleUnsavedDiscardNav : handleUnsavedDiscard}
          onCancel={handleUnsavedCancel}
        />

        {mainAgentId && (
          <TabPanel value={tabValue} index={1}>
            <DeploymentDetail agentId={mainAgentId} tenantId={Cookies.getTenantId()} />
          </TabPanel>
        )}

        {mainAgentId && (
          <TabPanel value={tabValue} index={2}>
            <ChannelIntegrations agentId={mainAgentId} tenantId={Cookies.getTenantId()} />
          </TabPanel>
        )}

        {mainAgentId && (
          <TabPanel value={tabValue} index={3}>
            <ToolsIntegrations agentId={mainAgentId} />
          </TabPanel>
        )}

        {mainAgentId && (
          <TabPanel value={tabValue} index={4} padding={{ xs: 0, sm: 3 }}>
            <BusinessActionSettings key={mainAgentId} agentId={mainAgentId} />
          </TabPanel>
        )}

      </Paper>
    </Container>
  );
}